import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import DichiarazioniBlock from "@/components/DichiarazioniBlock";
import ComuneAutocomplete from "@/components/common/ComuneAutocomplete";
import { TargaLookup } from "@/components/convenzioni/TargaLookup";
import { parseCodiceFiscale } from "@/lib/cf_parser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, Heart, Users, Compass, Upload, CheckCircle2, ArrowRight, ArrowLeft, Loader2, Tag, Building2, Shield, FileCheck, Home, Sparkles } from "lucide-react";
import { schemaFaq, schemaService, combineSchemas } from "@/lib/siteSchemas";
import { RcaWizard } from "@/pages/rca/RcaWizard";
import ContattoPreferenzeBlock from "@/components/ContattoPreferenzeBlock";

/**
 * ⚠️  PARENT ROUTER Convenzioni (v1.75.3)
 *
 * Questo file gestisce SOLO gli step iniziali del wizard convenzioni:
 *   - Selezione partner (Leonardo / Stellantis / GORI / UniCredit / GE Avio / Hitachi)
 *   - Selezione sede (solo per Leonardo)
 *   - Selezione tipo (auto / famiglia / tempo_libero)
 *
 * Dopo la scelta "tipo", delega a wizard dedicati:
 *   - RCA (auto)   → <RcaWizard>  in  /pages/rca/RcaWizard.jsx  (multi-step 8 passi)
 *   - Famiglia     → naviga a /questionario/{ramo}
 *   - Tempo libero → naviga a Viaggi / Fondi Pensione / etc.
 *
 * ❌ NON modificare il DatiPersonaliForm sottostante per feature RCA:
 *    quel form è ereditato dal wizard vecchio, non più raggiunto per tipo=auto.
 *    Per modifiche RCA vai su:
 *      - /pages/rca/StepXxxxx.jsx  (UI dei singoli step)
 *      - /hooks/useRcaWizard.js    (state + payload submit)
 */


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FAQ specifiche convenzioni per Long-tail SEO (es. "convenzione assicurazione dipendenti grande gruppo")
const CONVENZIONI_FAQS = [
  {
    q: "Come funziona la convenzione assicurativa per i dipendenti?",
    a: "Se sei dipendente di una delle aziende convenzionate con Colucci Assicuratori, accedi automaticamente a tariffe agevolate su polizze auto, famiglia e tempo libero. Ti basta verificare la tua appartenenza con busta paga e badge aziendale. Lo sconto si applica sia ai contratti nuovi che ai rinnovi.",
  },
  {
    q: "Quanto si risparmia con una convenzione aziendale?",
    a: "In media, lo sconto convenzione applicato sulle polizze auto va dal 10% al 25% rispetto alla tariffa standard, in funzione del profilo di rischio del dipendente, del veicolo e della classe di merito. Sulle polizze famiglia e tempo libero lo sconto medio e' del 15%.",
  },
  {
    q: "Quali aziende hanno convenzioni con Colucci Assicuratori?",
    a: "Abbiamo accordi attivi con realta' di primo piano nei settori difesa &amp; aerospazio, automotive, public utilities campane, banking europeo e motori aeronautici. La lista completa e' visibile selezionando il tuo datore di lavoro nella pagina Convenzioni.",
  },
  {
    q: "Sono dipendente Leonardo: posso accedere alla convenzione anche se lavoro a Pomigliano d'Arco?",
    a: "Si. La convenzione vale per tutte le 44 sedi operative del gruppo Leonardo distribuite su tutto il territorio nazionale, comprese le sedi campane di Pomigliano d'Arco, Nola, Bacoli, Giugliano e Benevento. La sede di Pomigliano e' particolarmente comoda perche' a 10 minuti dalla nostra agenzia.",
  },
  {
    q: "La convenzione e' valida sia per Leonardo Global Solutions che per Leonardo Logistics?",
    a: "Si. Il gruppo Leonardo include diverse societa' giuridiche (Leonardo Global Solutions, Leonardo Logistics, Leonardo S.p.A. e altre divisioni) che condividono spesso le stesse sedi operative. La convenzione vale per tutti i dipendenti del perimetro gruppo.",
  },
  {
    q: "Quali documenti servono per attivare la convenzione auto?",
    a: "Per la polizza auto in convenzione servono 4 documenti: ultima busta paga, badge aziendale fronte/retro, libretto di circolazione del veicolo, patente di guida. Tutti caricabili direttamente nel form, in PDF o foto da smartphone. Verifica completata entro 24 ore.",
  },
  {
    q: "La convenzione vale anche per i familiari del dipendente?",
    a: "Si. Lo sconto convenzione si estende al coniuge, ai figli conviventi e a tutto il nucleo familiare anagrafico del dipendente, sia per polizze auto che per polizze famiglia (casa, vita, infortuni, salute) e tempo libero (viaggi, sport, animali).",
  },
  {
    q: "Posso mantenere la convenzione anche dopo il pensionamento?",
    a: "Si, in molti casi e' previsto un mantenimento parziale dello sconto per ex dipendenti pensionati. La nostra consulenza e' personalizzata: durante la valutazione della tua pratica verifichiamo le condizioni applicabili al tuo profilo specifico.",
  },
  {
    q: "Sono in smart working: come faccio a fornire il badge?",
    a: "Va benissimo una foto del badge che hai a casa, anche scaduto da poco. Se non hai un badge fisico (lavoro completamente remoto), un'attestazione del datore di lavoro (carta intestata o email aziendale) puo' sostituirlo. Inviaci pure quello che hai e ti ricontattiamo per la verifica.",
  },
  {
    q: "Posso richiedere la convenzione tempo libero anche se non ho un'auto?",
    a: "Assolutamente si. Le convenzioni famiglia e tempo libero non richiedono veicolo ne' libretto. Coprono casa, vita, viaggi, sport, hobby, RC capofamiglia, animali domestici e altre necessita' del privato cittadino.",
  },
];

const TIPI = [
  { id: "auto", label: "RCA", desc: "Preventivo RC Auto in convenzione (wizard guidato)", icon: Car, color: "#163321", needsBuste: true },
  { id: "famiglia", label: "Famiglia", desc: "Casa, Vita, Infortuni, Salute", icon: Users, color: "#D97B58", needsBuste: false },
  { id: "tempo_libero", label: "Tempo Libero", desc: "Viaggi, sport, hobby, animali, RC capofamiglia", icon: Compass, color: "#7B5E3C", needsBuste: false },
];

export default function ConvenzioniPage() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [leonardoAziende, setLeonardoAziende] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);

  const [step, setStep] = useState(1);
  const [partner, setPartner] = useState(null);
  const [leonardoAzienda, setLeonardoAzienda] = useState("");
  const [sede, setSede] = useState("");
  const [tipo, setTipo] = useState(null);
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", telefono: "", extra: {}, privacy: false, mup: false, marketing: false, profilazione: false });
  const [bustaPagaId, setBustaPagaId] = useState(null);
  const [badgeAziendaId, setBadgeAziendaId] = useState(null);
  const [librettoId, setLibrettoId] = useState(null);
  const [librettoDonanteId, setLibrettoDonanteId] = useState(null);
  const [docDonanteId, setDocDonanteId] = useState(null);
  const [patenteId, setPatenteId] = useState(null);
  const [statoFamigliaId, setStatoFamigliaId] = useState(null);
  // Fase 5.4 — documento d'identità dell'assicurato (sempre obbligatorio in Auto)
  const [docIdentitaId, setDocIdentitaId] = useState(null);
  const [uploading, setUploading] = useState({ busta: false, badge: false, libretto: false, libretto_donante: false, doc_donante: false, patente: false, statoFamiglia: false, doc_identita: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  // Fase 5.1 — successo enriched (numero pratica + WA link cliente)
  const [successMeta, setSuccessMeta] = useState({ numeroPratica: "", waLinkCliente: "", waNumberBroker: "" });
  // Fase 5.1 — nuovi campi assuntivi (top-level, non extra)
  const [autoFields, setAutoFields] = useState({
    tipo_richiesta_auto: "",           // Fase 5.2 — Rinnovo/Nuova/Supporto
    // Fase 5.4 — `garanzie_interesse` RIMOSSO: le garanzie verranno discusse
    // in fase di preventivo/contatto commerciale, non nel lead iniziale.
    ricontatto_preferito: "",
    ricontatto_fascia_oraria: "",
    // Fase 5.4 — "Chi assicuriamo?" + Bersani esteso
    assicurato_target: "",                       // "" | "dipendente" | "familiare"
    familiare_assicurato: {                       // usato se assicurato_target=familiare
      titolare_nome: "", titolare_cognome: "",
      titolare_codice_fiscale: "", parentela: "",
    },
    bersani_source: "",                          // "" | "no" | "self" | "familiare_convivente"
    bersani_donor: { nome: "", cognome: "", parentela: "" },
    // Fase 5.3 — Rimossi da UI: uso/km/CU/sinistri (light assuntivi),
    // damed_veicolo_utilizzo, damed_data_immatricolazione, documenti_dopo toggle.
    // `documenti_dopo` resta implicitamente `true` server-side (docs sempre opzionali).
    // Fase 5.2 — DAMED IVASS (senza data_immatricolazione + veicolo_utilizzo)
    damed_coperture_attuali: [],
    damed_coperture_interesse: [],
    damed_grado_copertura: "",
    damed_rinuncia_rivalsa: false,
    damed_scatola_nera: false,
    damed_guidabene_risparmi: false,
    damed_clausole_facoltative: [],
    damed_note_supporto: "",
  });
  // veicolo unificato (targa+marca+modello) — sostituisce extra.targa/extra.modello
  const [veicolo, setVeicolo] = useState({ targa: "", marca: "", modello: "", alimentazione: "", anno: "" });
  // Autosave localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("conv_draft_v5");
      if (raw) {
        const d = JSON.parse(raw);
        if (d.form) setForm((f) => ({ ...f, ...d.form }));
        if (d.autoFields) setAutoFields((a) => ({ ...a, ...d.autoFields }));
        if (d.veicolo) setVeicolo((v) => ({ ...v, ...d.veicolo }));
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    axios.get(`${API}/convenzioni/partners`)
      .then(r => {
        setPartners(r.data?.partners || []);
        setLeonardoAziende(r.data?.leonardo_aziende || []);
      })
      .catch(() => setPartners([]))
      .finally(() => setLoadingPartners(false));
  }, []);

  const reset = () => {
    setStep(1); setPartner(null); setLeonardoAzienda(""); setSede(""); setTipo(null);
    setForm({ nome: "", cognome: "", email: "", telefono: "", extra: {}, privacy: false, mup: false, marketing: false, profilazione: false });
    setBustaPagaId(null); setBadgeAziendaId(null); setLibrettoId(null); setLibrettoDonanteId(null); setDocDonanteId(null); setPatenteId(null); setStatoFamigliaId(null); setDocIdentitaId(null);
    setError(""); setSuccess(false);
  };

  // Clear Leonardo-specific state when partner changes
  useEffect(() => {
    if (partner && !partner.has_sedi) {
      setLeonardoAzienda("");
      setSede("");
    }
  }, [partner]);

  const totalSteps = (() => {
    if (!partner) return 4;
    if (partner.has_sedi) return 4; // partner -> tipo -> sede -> dati (submit on step 4)
    return 3; // partner -> tipo -> dati (submit on step 3)
  })();

  const canNext = () => {
    if (step === 1) return !!partner;
    if (step === 2) return !!tipo;
    if (step === 3 && partner?.has_sedi) return !!leonardoAzienda && !!sede;
    if (step === 3 && !partner?.has_sedi) return canSubmit();
    if (step === 4) return canSubmit();
    return false;
  };

  const canSubmit = () => {
    const f = form;
    const baseOk = f.nome && f.cognome && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email) && /^[+\d\s]{6,30}$/.test(f.telefono) && f.privacy && f.mup;
    if (!baseOk) return false;

    // Fase 5.4 — Regole "Chi assicuriamo?" per il flow Auto (tutti i partner)
    if (tipo?.id === "auto") {
      const at = autoFields.assicurato_target;
      // Il target deve essere scelto
      if (!at) return false;
      // Doc identità sempre obbligatorio in Auto
      if (!docIdentitaId) return false;
      if (at === "familiare") {
        // Stato famiglia obbligatorio + info titolare completa
        if (!statoFamigliaId) return false;
        const fa = autoFields.familiare_assicurato || {};
        if (!fa.titolare_nome || !fa.titolare_cognome) return false;
        const cfTit = (fa.titolare_codice_fiscale || "").toUpperCase().replace(/\s/g, "");
        if (!/^[A-Z0-9]{11,16}$/.test(cfTit)) return false;
        if (!fa.parentela) return false;
      }
      // Bersani esteso: se familiare_convivente, servono info donatore + stato famiglia
      if (autoFields.bersani_source === "familiare_convivente") {
        const bd = autoFields.bersani_donor || {};
        if (!bd.nome || !bd.cognome || !bd.parentela) return false;
        if (!statoFamigliaId) return false;
      }
    }

    // Fase 5.3: documenti sempre opzionali (rimosso toggle). L'agenzia li chiederà
    // via WhatsApp post-preventivo. Nessuna validazione documenti al submit.
    if (tipo?.needsBuste) {
      // (no-op) — validazione documenti rimossa. Vedi Fase 5.3.

      // Se Leonardo, servono anche tutti i campi extra anagrafici e la matricola coincidente
      if (partner?.id === "leonardo") {
        const e = form.extra || {};
      // Se l'utente sceglie "Aiutami a capire", basta che abbia inserito i contatti base + caricato i 4 documenti.
      // Salta tutta la validazione tecnica: il consulente Colucci farà tutto.
      if (e.tipo_richiesta === "non_so") {
        return baseOk;
      }
        const cfOk = /^[A-Za-z0-9]{11,16}$/.test((e.codice_fiscale || "").replace(/\s/g, ""));
        const capOk = /^\d{5}$/.test((e.cap || "").trim());
        const provOk = /^[A-Za-z]{2}$/.test((e.provincia || "").trim());
        const matrOk = !!e.matricola && (e.matricola || "").length >= 3;
        const baseExtraOk = !!(e.indirizzo && e.localita && e.tipo_veicolo);
        // Tipo richiesta supportata
        const tipoRichiestaOk = ["rinnovo", "nuovo_acquisto", "recupero_attestato"].includes(e.tipo_richiesta);
        // Eredità classe: se attivata, targa donante + tipo + dichiarazione no sinistri 5 anni + libretto donante
        let ereditaOk = true;
        if (e.eredita_classe) {
          const targaOk = /^[A-Z0-9]{5,8}$/.test((e.targa_veicolo_donante || "").toUpperCase().replace(/\s/g, ""));
          const tipoDonanteOk = ["auto", "moto", "autocarro"].includes(e.tipo_veicolo_donante);
          const sinistriOk = !!e.donante_no_sinistri_5anni;
          const librettoDonanteOk = !!librettoDonanteId;
          const docDonanteOk = !!docDonanteId;
            // Bersani vincolo stesso tipo veicolo (solo per "nuovo_acquisto")
          const bersaniOk = e.tipo_richiesta !== "nuovo_acquisto" || (e.tipo_veicolo_donante === e.tipo_veicolo);
          ereditaOk = targaOk && tipoDonanteOk && sinistriOk && librettoDonanteOk && docDonanteOk && bersaniOk;
        }
        // Stato famiglia obbligatorio se: rc_familiare OR contraente_diverso OR (eredita_classe AND donante_familiare_convivente)
        const needsSF = e.rc_familiare || e.contraente_diverso || (e.eredita_classe && e.donante_familiare_convivente);
        const sfOk = !needsSF || !!statoFamigliaId;
        // Se contraente diverso, dati anagrafici contraente obbligatori
        let contraenteOk = true;
        if (e.contraente_diverso) {
          const cfContrOk = /^[A-Za-z0-9]{11,16}$/.test((e.contraente_codice_fiscale || "").replace(/\s/g, ""));
          contraenteOk = !!e.contraente_nome && !!e.contraente_cognome && cfContrOk;
        }
        return cfOk && capOk && provOk && matrOk && baseExtraOk && tipoRichiestaOk && ereditaOk && sfOk && contraenteOk;
      }
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      // Fase 5.1 — merge veicolo top-level e extra (retrocompatibile)
      const mergedExtra = { ...form.extra };
      if (veicolo.targa) mergedExtra.targa = veicolo.targa;
      if (veicolo.marca || veicolo.modello) {
        mergedExtra.modello = [veicolo.marca, veicolo.modello].filter(Boolean).join(" ").trim();
      }
      if (veicolo.alimentazione) mergedExtra.alimentazione = veicolo.alimentazione;
      if (veicolo.anno) mergedExtra.anno = String(veicolo.anno);

      const payload = {
        partner: partner.id,
        leonardo_azienda: partner.id === "leonardo" ? leonardoAzienda : "",
        sede: partner.has_sedi ? sede : "",
        tipo: tipo.id,
        nome: form.nome, cognome: form.cognome, email: form.email, telefono: form.telefono,
        extra: mergedExtra,
        privacy_accettata: form.privacy,
        mup_accettato: form.mup,
        marketing_consenso: form.marketing,
        profilazione_consenso: form.profilazione,
        busta_paga_id: bustaPagaId,
        badge_azienda_id: badgeAziendaId,
        libretto_id: librettoId,
        libretto_donante_id: librettoDonanteId,
        doc_donante_id: docDonanteId,
        patente_id: patenteId,
        stato_famiglia_id: statoFamigliaId,
        // Fase 5.1 — campi (Fase 5.3: rimossi uso/km/CU/sinistri + documenti_dopo toggle)
        //           (Fase 5.4: rimosso garanzie_interesse dal payload)
        documenti_dopo: true,   // Fase 5.3 — sempre true: docs sempre opzionali
        ricontatto_preferito: autoFields.ricontatto_preferito || null,
        ricontatto_fascia_oraria: autoFields.ricontatto_fascia_oraria || null,
        // Fase 5.2 — DAMED IVASS + Tipo richiesta (Fase 5.3: rimossi utilizzo + data_immatricolazione)
        tipo_richiesta_auto: autoFields.tipo_richiesta_auto || null,
        // Fase 5.4 — "Chi assicuriamo?" + Bersani esteso
        assicurato_target: autoFields.assicurato_target || null,
        familiare_assicurato: autoFields.familiare_assicurato || null,
        bersani_source: autoFields.bersani_source || null,
        bersani_donor: autoFields.bersani_donor || null,
        doc_identita_id: docIdentitaId,
        stato_famiglia_doc_id: statoFamigliaId,
        damed_coperture_attuali: autoFields.damed_coperture_attuali,
        damed_coperture_interesse: autoFields.damed_coperture_interesse,
        damed_grado_copertura: autoFields.damed_grado_copertura || null,
        damed_rinuncia_rivalsa: !!autoFields.damed_rinuncia_rivalsa,
        damed_scatola_nera: !!autoFields.damed_scatola_nera,
        damed_guidabene_risparmi: !!autoFields.damed_guidabene_risparmi,
        damed_clausole_facoltative: autoFields.damed_clausole_facoltative,
        damed_note_supporto: autoFields.damed_note_supporto || null,
      };
      const res = await axios.post(`${API}/convenzioni/lead`, payload);
      const data = res?.data || {};
      setSuccessMeta({
        numeroPratica: data.numero_pratica || "",
        waLinkCliente: data.wa_link_cliente || "",
        waNumberBroker: data.wa_number_broker || "",
      });
      setSuccess(true);
      try { localStorage.removeItem("conv_draft_v5"); } catch { /* ignore */ }
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore invio richiesta. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  // Fase 5.1 — Autosave debouncing (500ms)
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem("conv_draft_v5", JSON.stringify({
          form: { nome: form.nome, cognome: form.cognome, email: form.email,
                   telefono: form.telefono, extra: form.extra },
          autoFields, veicolo,
        }));
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(t);
  }, [form, autoFields, veicolo]);

  // Fase 5.1 — Parser CF: auto-fill CAP/provincia se CF valido
  useEffect(() => {
    const cf = (form.extra?.codice_fiscale || "").toUpperCase().replace(/\s/g, "");
    if (cf.length !== 16) return;
    const parsed = parseCodiceFiscale(cf);
    if (parsed.valid) {
      // Log audit ma non forziamo override manuale utente
      // CAP/provincia non derivabili senza tabella catastale → skip
      // (Data nascita/sesso disponibili in parsed.dataNascita/parsed.sesso se serve)
    }
  }, [form.extra?.codice_fiscale]);

  // ===================== RENDER =====================

  if (success) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20 flex items-center justify-center px-5">
          <div className="max-w-md w-full text-center" data-testid="convenzioni-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="font-heading text-3xl font-medium text-[#0F1F14] mb-3">Richiesta ricevuta!</h1>
            {successMeta.numeroPratica && (
              <p className="text-xs text-[#4A5E51] mb-2 font-mono"
                  data-testid="numero-pratica">
                Numero pratica: <strong>#{successMeta.numeroPratica}</strong>
              </p>
            )}
            <p className="text-sm text-[#4A5E51] mb-6">
              Grazie {form.nome}. Un nostro consulente dedicato alle convenzioni{" "}
              <strong>{partner.name}</strong> ti contatterà entro 24 ore con la
              proposta su misura.
            </p>

            {/* Fase 5.1 — WhatsApp CTA cliente → Colucci */}
            {successMeta.waLinkCliente && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-5 text-left"
                    data-testid="wa-cta-block">
                <p className="text-xs text-emerald-900 mb-3">
                  💬 Vuoi anticipare qualcosa via <strong>WhatsApp</strong>? Il
                  nostro agente ti risponderà subito.
                </p>
                <a href={successMeta.waLinkCliente}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="wa-link-cliente"
                    className="w-full inline-flex items-center justify-center gap-2
                                h-11 rounded-full bg-[#25D366] hover:bg-[#1EBE5D]
                                text-white text-sm font-medium transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                        fill="currentColor" className="w-5 h-5" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.83 9.83 0 016.994 2.899 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Apri WhatsApp con Colucci
                </a>
                {successMeta.waNumberBroker && (
                  <p className="text-[10px] text-emerald-700 text-center mt-2">
                    Numero agenzia: {successMeta.waNumberBroker}
                  </p>
                )}
              </div>
            )}

            <Link to="/"
                   className="inline-flex items-center gap-2 px-6 h-11 bg-[#163321] text-white rounded-full text-sm font-medium hover:bg-[#224A32]"
                   data-testid="back-home-btn">
              Torna alla home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(combineSchemas(
            schemaService({
              name: "Convenzioni Assicurative Aziendali",
              description: "Polizze auto, famiglia e tempo libero a tariffe agevolate per dipendenti di grandi gruppi industriali italiani (difesa, automotive, public utilities, banking, motori aeronautici).",
              slug: "convenzioni",
              serviceType: "InsuranceProductService",
            }),
            schemaFaq(CONVENZIONI_FAQS),
          )),
        }}
      />
      <main
        className="min-h-screen bg-[#FCFCFA] pt-20"
        data-testid="convenzioni-page"
        style={partner?.color ? {
          // Fase 5.4 — Colore partner mixato al 30% con brand Colucci (70/30).
          // Applicato come CSS variable a livello di container per accenti/badge.
          "--colucci-primary": "#163321",
          "--partner-color": partner.color,
          "--brand-accent": `color-mix(in srgb, var(--colucci-primary) 70%, var(--partner-color) 30%)`,
        } : undefined}
      >
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F1F14] via-[#163321] to-[#1d4a2e] text-white">
          <div className="max-w-4xl mx-auto px-5 sm:px-6 py-12 sm:py-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D97B58]/15 border border-[#D97B58]/30 rounded-full text-xs text-[#D97B58] font-medium mb-4">
              <Tag className="w-3 h-3" /> Convenzioni Aziendali
            </div>
            <h1 className="font-heading text-3xl sm:text-5xl font-medium mb-3">
              Lavori in un grande gruppo industriale?<br />
              <span className="text-[#D97B58]">Hai sconti dedicati.</span>
            </h1>
            <p className="text-sm sm:text-base text-white/70 max-w-2xl">
              Colucci Assicuratori ha accordi attivi con realtà di primo piano nei settori difesa &amp; aerospazio,
              automotive, public utilities, banking e motori aeronautici. Polizze auto, famiglia e tempo libero
              a tariffe agevolate riservate ai dipendenti.
            </p>
          </div>
        </section>

        {/* Progress bar */}
        <div className="sticky top-20 z-20 bg-[#FCFCFA]/95 backdrop-blur-sm border-b border-[#E5E2DB]" data-testid="convenzioni-progress">
          <div className="max-w-4xl mx-auto px-5 sm:px-6 py-3">
            <div className="flex items-center gap-2 mb-1.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-all ${i < step ? "bg-[var(--brand-accent,#163321)]" : "bg-[#E5E2DB]"}`}
                />
              ))}
            </div>
            <p className="text-[10px] text-[#4A5E51] uppercase tracking-wider">Passo {step} di {totalSteps}</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10">
          {/* STEP 1: Partner */}
          {step === 1 && (
            <div data-testid="step-partner">
              <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-2">Per quale azienda lavori?</h2>
              <p className="text-sm text-[#4A5E51] mb-6">Seleziona il tuo datore di lavoro per accedere alle tariffe convenzionate.</p>
              {loadingPartners ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#163321]" /></div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {partners.map(p => {
                    const isActive = partner?.id === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPartner(p)}
                        className={`text-left p-5 rounded-2xl border-2 transition-all ${
                          isActive ? "border-[#163321] bg-[#163321]/5 shadow-md" : "border-[#E5E2DB] bg-white hover:border-[#163321]/40"
                        }`}
                        data-testid={`partner-${p.id}`}
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-2xl shrink-0" style={{ backgroundColor: `${p.color}15`, color: p.color }}>
                            {p.logo_emoji}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-[#0F1F14] text-sm">{p.name}</p>
                            <p className="text-[11px] text-[#4A5E51]">{p.tagline}</p>
                          </div>
                          {isActive && <CheckCircle2 className="w-4 h-4 text-[#163321] shrink-0" />}
                        </div>
                        {p.has_sedi && (
                          <p className="text-[10px] text-[#D97B58] font-medium">📍 {p.sedi.length} sedi disponibili</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Tipo polizza */}
          {step === 2 && (
            <div data-testid="step-tipo">
              <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-2">Cosa vuoi assicurare?</h2>
              <p className="text-sm text-[#4A5E51] mb-6">Le convenzioni {partner?.name} sono attive su queste aree.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {TIPI.map(t => {
                  const isActive = tipo?.id === t.id;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTipo(t)}
                      className={`text-left p-5 rounded-2xl border-2 transition-all ${
                        isActive ? "border-[#163321] bg-[#163321]/5 shadow-md" : "border-[#E5E2DB] bg-white hover:border-[#163321]/40"
                      }`}
                      data-testid={`tipo-${t.id}`}
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${t.color}15`, color: t.color }}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <p className="font-semibold text-[#0F1F14] text-base">{t.label}</p>
                      <p className="text-[11px] text-[#4A5E51] mt-1">{t.desc}</p>
                      {t.needsBuste && (
                        <p className="text-[10px] text-[#D97B58] mt-2 font-medium">Richiede busta paga + badge aziendale</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 3: Sede (solo Leonardo) o Dati personali (per altri) */}
          {step === 3 && partner?.has_sedi && (
            <div data-testid="step-sede">
              <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-2">Dove lavori in {partner.name}?</h2>
              <p className="text-sm text-[#4A5E51] mb-6">
                Il gruppo Leonardo include più società giuridiche con sedi spesso condivise.
                Indicaci la tua società di assunzione e la sede operativa.
              </p>

              {/* Azienda del gruppo Leonardo */}
              <div className="mb-5">
                <Label className="text-xs">Società del gruppo *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                  {leonardoAziende.map(a => {
                    const isActive = leonardoAzienda === a.id;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setLeonardoAzienda(a.id)}
                        className={`p-3 rounded-xl border-2 text-sm transition-all text-left ${
                          isActive ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] hover:border-[#163321]/40 bg-white"
                        }`}
                        data-testid={`leonardo-azienda-${a.id}`}
                      >
                        {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-[#163321] mb-1" />}
                        <p className="font-medium text-[#0F1F14] text-xs leading-tight">{a.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Sede operativa */}
              <div>
                <Label className="text-xs">Sede operativa *</Label>
                <Select value={sede} onValueChange={setSede}>
                  <SelectTrigger className="w-full h-12 rounded-xl mt-2" data-testid="sede-select">
                    <SelectValue placeholder="Seleziona la tua sede operativa..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {partner.sedi.map(s => (
                      <SelectItem key={s.id} value={s.id} data-testid={`sede-opt-${s.id}`}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">{s.label}</span>
                          <span className="text-[10px] text-zinc-500">{s.address}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* STEP 3 (no sedi) o STEP 4 (con sedi): Dati personali */}
          {((step === 3 && !partner?.has_sedi) || (step === 4 && partner?.has_sedi)) && (
            tipo?.id === "auto" ? (
              // Fase 5.5 — Nuovo wizard RCA (7 step interni, submit autonomo)
              <RcaWizard
                partner={partner}
                sede={sede}
                onBackToTipo={() => { setError(""); setStep(s => s - 1); }}
              />
            ) : tipo?.id === "famiglia" ? (
              // FASE I — Sub-selettore Famiglia: 4 rami (Casa/Infortuni/Vita/Salute)
              // che portano al wizard `QuestionnairePage` corrispondente.
              <FamigliaBranchSelector
                partner={partner} sede={sede}
                onSelect={(ramo) => {
                  const params = new URLSearchParams();
                  if (partner?.id) params.set("partner", partner.id);
                  if (sede) params.set("sede", sede);
                  if (leonardoAzienda) params.set("azienda", leonardoAzienda);
                  navigate(`/questionario/${ramo}?${params.toString()}`);
                }}
              />
            ) : tipo?.id === "tempo_libero" ? (
              // FASE J — Sub-selettore Tempo Libero: Viaggi attivo,
              // Sport/Hobby/Animali placeholder (Prossimamente).
              <TempoLiberoBranchSelector
                partner={partner} sede={sede}
                onSelect={(ramo) => {
                  const params = new URLSearchParams();
                  if (partner?.id) params.set("partner", partner.id);
                  if (sede) params.set("sede", sede);
                  if (leonardoAzienda) params.set("azienda", leonardoAzienda);
                  navigate(`/questionario/${ramo}?${params.toString()}`);
                }}
              />
            ) : (
              <DatiPersonaliForm
                form={form} setForm={setForm}
                tipo={tipo} partner={partner}
                leonardoAzienda={leonardoAzienda} leonardoAziende={leonardoAziende}
                bustaPagaId={bustaPagaId} setBustaPagaId={setBustaPagaId}
                badgeAziendaId={badgeAziendaId} setBadgeAziendaId={setBadgeAziendaId}
                librettoId={librettoId} setLibrettoId={setLibrettoId}
                librettoDonanteId={librettoDonanteId} setLibrettoDonanteId={setLibrettoDonanteId}
                docDonanteId={docDonanteId} setDocDonanteId={setDocDonanteId}
                patenteId={patenteId} setPatenteId={setPatenteId}
                statoFamigliaId={statoFamigliaId} setStatoFamigliaId={setStatoFamigliaId}
                docIdentitaId={docIdentitaId} setDocIdentitaId={setDocIdentitaId}
                uploading={uploading} setUploading={setUploading}
                error={error} setError={setError}
                autoFields={autoFields} setAutoFields={setAutoFields}
                veicolo={veicolo} setVeicolo={setVeicolo}
              />
            )
          )}

          {/* Navigation — nascosta durante il wizard RCA (che ha nav interna) e durante i selettori Famiglia/Tempo Libero */}
          {!((tipo?.id === "auto" || tipo?.id === "famiglia" || tipo?.id === "tempo_libero")
              && ((step === 3 && !partner?.has_sedi) || (step === 4 && partner?.has_sedi))) && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E5E2DB]">
            {step > 1 ? (
              <Button variant="outline" onClick={() => { setError(""); setStep(s => s - 1); }} className="rounded-full" data-testid="btn-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
            ) : <div />}

            {((step === 4 && partner?.has_sedi) || (step === 3 && !partner?.has_sedi)) ? (
              <Button onClick={submit} disabled={!canSubmit() || submitting}
                className="rounded-full bg-[#163321] hover:bg-[#224A32] disabled:opacity-50" data-testid="btn-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</> : <>Invia richiesta <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            ) : (
              <Button onClick={() => { setError(""); setStep(s => s + 1); }} disabled={!canNext()}
                className="rounded-full bg-[#163321] hover:bg-[#224A32] disabled:opacity-50" data-testid="btn-next">
                Avanti <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
          )}

          {error && <p className="mt-4 text-sm text-red-600 text-center" data-testid="form-error">{error}</p>}
        </div>

        {/* ===== FAQ visibili per SEO + utente ===== */}
        <section className="bg-white border-t border-[#E5E2DB] py-14" data-testid="convenzioni-faq-section">
          <div className="max-w-3xl mx-auto px-5 sm:px-6">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#D97B58] font-semibold mb-3 text-center">Domande frequenti</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#0F1F14] mb-8 text-center">
              Tutto quello che devi sapere<br />sulle convenzioni assicurative
            </h2>
            <div className="space-y-3">
              {CONVENZIONI_FAQS.map((f, i) => (
                <details key={i} className="group bg-[#FCFCFA] border border-[#E5E2DB] rounded-xl overflow-hidden" data-testid={`conv-faq-${i}`}>
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-3 p-4 sm:p-5 hover:bg-[#163321]/[0.02] transition-colors">
                    <h3 className="text-sm sm:text-base font-medium text-[#0F1F14] flex-1">{f.q}</h3>
                    <span className="text-[#163321] shrink-0 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                  </summary>
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-sm text-[#4A5E51] leading-relaxed" dangerouslySetInnerHTML={{ __html: f.a }} />
                </details>
              ))}
            </div>
            <p className="text-center mt-8 text-xs text-[#4A5E51]">
              Hai altre domande? <a href="tel:0817611000" className="text-[#163321] underline font-medium">Chiamaci al 081 761 1000</a>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

// ==================== Sotto-componente Dati Personali ====================
function DatiPersonaliForm({ form, setForm, tipo, partner, leonardoAzienda, leonardoAziende, bustaPagaId, setBustaPagaId, badgeAziendaId, setBadgeAziendaId, librettoId, setLibrettoId, librettoDonanteId, setLibrettoDonanteId, docDonanteId, setDocDonanteId, patenteId, setPatenteId, statoFamigliaId, setStatoFamigliaId, docIdentitaId, setDocIdentitaId, uploading, setUploading, error, setError, autoFields, setAutoFields, veicolo, setVeicolo }) {
  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const updateExtra = (field, value) => setForm(f => ({ ...f, extra: { ...f.extra, [field]: value } }));
  const [extracted, setExtracted] = useState({});  // tracks which doc types have been auto-extracted

  // Estrae automaticamente i dati dal documento appena caricato e popola i campi corrispondenti.
  // Solo se i campi sono ancora vuoti — non sovrascrive ciò che l'utente ha già compilato.
  const autoExtractFromFile = async (file, tipo_documento) => {
    try {
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await axios.post(`${API}/documenti/analizza`, { image_base64: b64, tipo_documento });
      const d = r.data?.dati || {};
      setForm(f => {
        const next = { ...f, extra: { ...f.extra } };
        // Patente / Identità → anagrafica utente
        if (tipo_documento === "patente" || tipo_documento === "identita") {
          if (d.nome && !f.nome) next.nome = d.nome;
          if (d.cognome && !f.cognome) next.cognome = d.cognome;
          if (d.codice_fiscale && !f.extra.codice_fiscale) next.extra.codice_fiscale = d.codice_fiscale.toUpperCase();
          const ind = d.indirizzo || d.indirizzo_residenza;
          if (ind && !f.extra.indirizzo) next.extra.indirizzo = ind;
          if (d.comune_residenza && !f.extra.localita) next.extra.localita = d.comune_residenza;
        }
        // Libretto → veicolo
        if (tipo_documento === "libretto") {
          if (d.targa && !f.extra.targa) next.extra.targa = (d.targa || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
          if ((d.marca || d.modello) && !f.extra.modello) next.extra.modello = `${d.marca || ""} ${d.modello || ""}`.trim();
          if (!f.extra.tipo_veicolo) {
            const desc = `${d.modello || ""} ${d.allestimento || ""}`.toLowerCase();
            if (desc.includes("moto")) next.extra.tipo_veicolo = "moto";
            else if (d.massa_max && parseInt(d.massa_max) > 3500) next.extra.tipo_veicolo = "autocarro";
            else next.extra.tipo_veicolo = "auto";
          }
        }
        // Busta paga → matricola + contatti
        if (tipo_documento === "busta_paga") {
          if (d.matricola && !f.extra.matricola) next.extra.matricola = String(d.matricola).replace(/[^A-Za-z0-9-]/g, "").slice(0, 20);
          if (d.codice_fiscale && !f.extra.codice_fiscale) next.extra.codice_fiscale = d.codice_fiscale.toUpperCase();
          if (d.cognome && !f.cognome) next.cognome = d.cognome;
          if (d.nome && !f.nome) next.nome = d.nome;
          if (d.indirizzo_residenza && !f.extra.indirizzo) next.extra.indirizzo = d.indirizzo_residenza;
          if (d.comune_residenza && !f.extra.localita) next.extra.localita = d.comune_residenza;
          if (d.cap && !f.extra.cap) next.extra.cap = String(d.cap).replace(/\D/g, "").slice(0, 5);
          if (d.provincia && !f.extra.provincia) next.extra.provincia = String(d.provincia).toUpperCase().slice(0, 2);
        }
        return next;
      });
      setExtracted(prev => ({ ...prev, [tipo_documento]: true }));
    } catch (e) {
      // Non bloccare l'upload se l'estrazione AI fallisce — l'utente può sempre compilare a mano
      console.warn("[AutoExtract]", tipo_documento, e?.response?.data?.detail || e?.message);
    }
  };

  const uploadFile = async (file, kind) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("File troppo grande (max 8 MB)");
      return;
    }
    setError("");
    setUploading(u => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fid = r.data?.id;
      if (!fid) throw new Error("Upload fallito");
      if (kind === "busta") setBustaPagaId(fid);
      else if (kind === "badge") setBadgeAziendaId(fid);
      else if (kind === "libretto") setLibrettoId(fid);
      else if (kind === "patente") setPatenteId(fid);
      else if (kind === "stato_famiglia") setStatoFamigliaId(fid);
      else if (kind === "libretto_donante") setLibrettoDonanteId(fid);
      else if (kind === "doc_donante") setDocDonanteId(fid);
      else if (kind === "doc_identita") setDocIdentitaId(fid);

      // Auto-estrazione AI per i documenti supportati (best-effort, non bloccante)
      const extractMap = { busta: "busta_paga", libretto: "libretto", patente: "patente", libretto_donante: "libretto", doc_donante: "identita", doc_identita: "identita" };
      const docType = extractMap[kind];
      if (docType && file.type.startsWith("image/")) {
        autoExtractFromFile(file, docType);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore upload file");
    } finally {
      setUploading(u => ({ ...u, [kind]: false }));
    }
  };

  return (
    <div data-testid="step-dati">
      <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-2">I tuoi dati</h2>
      <p className="text-sm text-[#4A5E51] mb-6">
        Tutti i dati sono trattati secondo GDPR. Solo per consulenza assicurativa privata.
      </p>

      {/* Banner profilo selezionato */}
      <div
        className="mb-6 p-3 bg-[#163321]/5 border border-[#163321]/20 rounded-lg flex items-center gap-2 text-xs"
        style={{
          // Fase 5.4 — accento partner (border-left colorato + tinta bg)
          borderLeft: "4px solid var(--brand-accent, #163321)",
        }}
        data-testid="banner-profilo-partner"
      >
        <Building2 className="w-4 h-4 text-[#163321]" />
        <span className="text-[#163321]">
          <strong>{partner.name}</strong>
          {leonardoAzienda && leonardoAziende?.length > 0 && (
            <> · {(leonardoAziende.find(a => a.id === leonardoAzienda) || {}).label}</>
          )}
          {" · "}{tipo.label}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Nome *</Label>
          <Input value={form.nome} onChange={e => update("nome", e.target.value)} placeholder="Mario" data-testid="input-nome" />
        </div>
        <div>
          <Label className="text-xs">Cognome *</Label>
          <Input value={form.cognome} onChange={e => update("cognome", e.target.value)} placeholder="Rossi" data-testid="input-cognome" />
        </div>
        <div>
          <Label className="text-xs">Email *</Label>
          <Input type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="mario.rossi@email.com" data-testid="input-email" />
        </div>
        <div>
          <Label className="text-xs">Telefono *</Label>
          <Input type="tel" value={form.telefono} onChange={e => update("telefono", e.target.value)} placeholder="333 1234567" data-testid="input-telefono" />
        </div>
      </div>

      {/* Extra per tipo polizza */}
      <div className="mt-6 space-y-4">
        {tipo.id === "auto" && (
          <>
            {/* ================================================================ */}
            {/* Fase 5.4 — "Chi assicuriamo?" (dipendente vs familiare)          */}
            {/* ================================================================ */}
            <div
              className="rounded-2xl border-2 bg-white p-4 space-y-3"
              style={{ borderColor: "var(--brand-accent, #163321)" }}
              data-testid="chi-assicuriamo-block"
            >
              <Label className="text-sm font-medium text-[#0F1F14]">
                Chi assicuriamo? <span className="text-[#7A1D1D]">*</span>
              </Label>
              <p className="text-[11px] text-[#4A5E51] -mt-1">
                La convenzione {partner?.name} vale sia per te (dipendente) sia per i familiari conviventi.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  {
                    k: "dipendente",
                    t: "Dipendente convenzionato",
                    d: "Sono io il dipendente / collaboratore convenzionato",
                  },
                  {
                    k: "familiare",
                    t: "Familiare del dipendente",
                    d: "Sono un familiare convivente del dipendente (coniuge, figlio, genitore…)",
                  },
                ].map((o) => {
                  const on = autoFields.assicurato_target === o.k;
                  return (
                    <button
                      key={o.k}
                      type="button"
                      onClick={() => setAutoFields({ ...autoFields, assicurato_target: o.k })}
                      data-testid={`radio-assicurato-target-${o.k}`}
                      className={`text-left rounded-xl border p-3 transition-all ${
                        on
                          ? "border-[var(--brand-accent,#163321)] bg-[#163321]/5 ring-2 ring-[var(--brand-accent,#163321)]/20"
                          : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
                      }`}
                    >
                      <div className="text-sm font-medium text-[#0F1F14]">{o.t}</div>
                      <div className="text-[11px] text-[#4A5E51] mt-0.5">{o.d}</div>
                    </button>
                  );
                })}
              </div>

              {/* Se familiare → dati del dipendente titolare */}
              {autoFields.assicurato_target === "familiare" && (
                <div className="pt-2 border-t border-[#E5E2DB] space-y-3" data-testid="familiare-titolare-block">
                  <p className="text-[11px] font-semibold text-[#0F1F14]">
                    Dati del dipendente titolare della convenzione
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Nome titolare *</Label>
                      <Input
                        value={autoFields.familiare_assicurato.titolare_nome}
                        onChange={(e) =>
                          setAutoFields({
                            ...autoFields,
                            familiare_assicurato: {
                              ...autoFields.familiare_assicurato,
                              titolare_nome: e.target.value,
                            },
                          })
                        }
                        data-testid="input-familiare-titolare-nome"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Cognome titolare *</Label>
                      <Input
                        value={autoFields.familiare_assicurato.titolare_cognome}
                        onChange={(e) =>
                          setAutoFields({
                            ...autoFields,
                            familiare_assicurato: {
                              ...autoFields.familiare_assicurato,
                              titolare_cognome: e.target.value,
                            },
                          })
                        }
                        data-testid="input-familiare-titolare-cognome"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Codice fiscale titolare *</Label>
                      <Input
                        value={autoFields.familiare_assicurato.titolare_codice_fiscale}
                        onChange={(e) =>
                          setAutoFields({
                            ...autoFields,
                            familiare_assicurato: {
                              ...autoFields.familiare_assicurato,
                              titolare_codice_fiscale: e.target.value
                                .toUpperCase()
                                .replace(/\s/g, ""),
                            },
                          })
                        }
                        maxLength={16}
                        data-testid="input-familiare-titolare-cf"
                        placeholder="RSSMRA80A01H501Z"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Parentela con il titolare *</Label>
                      <Select
                        value={autoFields.familiare_assicurato.parentela}
                        onValueChange={(val) =>
                          setAutoFields({
                            ...autoFields,
                            familiare_assicurato: {
                              ...autoFields.familiare_assicurato,
                              parentela: val,
                            },
                          })
                        }
                      >
                        <SelectTrigger
                          className="h-10 text-sm"
                          data-testid="select-familiare-parentela"
                        >
                          <SelectValue placeholder="Seleziona parentela" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="coniuge">Coniuge</SelectItem>
                          <SelectItem value="padre">Padre</SelectItem>
                          <SelectItem value="madre">Madre</SelectItem>
                          <SelectItem value="figlio">Figlio</SelectItem>
                          <SelectItem value="figlia">Figlia</SelectItem>
                          <SelectItem value="fratello">Fratello</SelectItem>
                          <SelectItem value="sorella">Sorella</SelectItem>
                          <SelectItem value="altro">Altro convivente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Doc identità (obbligatorio sempre in Auto) */}
            {autoFields.assicurato_target && (
              <div className="pt-2" data-testid="doc-identita-block">
                <FileUploadCard
                  label={
                    autoFields.assicurato_target === "familiare"
                      ? "Documento d'identità dell'assicurato (familiare) *"
                      : "Documento d'identità del dipendente assicurato *"
                  }
                  description="Carta d'identità o patente in corso di validità — fronte/retro"
                  fileId={docIdentitaId}
                  uploading={uploading.doc_identita}
                  onChange={(f) => uploadFile(f, "doc_identita")}
                  onClear={() => setDocIdentitaId(null)}
                  testid="upload-doc-identita"
                />
              </div>
            )}

            {/* Stato famiglia obbligatorio se assicurato = familiare */}
            {autoFields.assicurato_target === "familiare" && (
              <div className="pt-2" data-testid="stato-famiglia-block-54">
                <FileUploadCard
                  label="Stato di famiglia *"
                  description="Certificato del Comune o autocertificazione — necessario per verificare la convivenza con il dipendente titolare"
                  fileId={statoFamigliaId}
                  uploading={uploading.stato_famiglia}
                  onChange={(f) => uploadFile(f, "stato_famiglia")}
                  onClear={() => setStatoFamigliaId(null)}
                  testid="upload-stato-famiglia-54"
                />
              </div>
            )}

            {/* Fase 5.2 — Branching Tipo Richiesta (Rinnovo/Nuova/Supporto) */}
            <div className="rounded-2xl border border-[#E5E2DB] bg-white p-4 space-y-3"
                  data-testid="tipo-richiesta-auto-block">
              <Label className="text-sm font-medium text-[#0F1F14]">
                Cosa ti serve? <span className="text-[#7A1D1D]">*</span>
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { k: "rinnovo",       t: "Rinnovo",        d: "Ho già una polizza, voglio rinnovarla" },
                  { k: "nuova_polizza", t: "Nuova polizza",  d: "Prima assicurazione o cambio compagnia" },
                  { k: "supporto",      t: "Supporto",       d: "Ho bisogno di aiuto per capire" },
                ].map((o) => {
                  const on = autoFields.tipo_richiesta_auto === o.k;
                  return (
                    <button key={o.k} type="button"
                             onClick={() => setAutoFields({ ...autoFields, tipo_richiesta_auto: o.k })}
                             data-testid={`radio-tipo-richiesta-${o.k}`}
                             className={`text-left rounded-xl border p-3 transition-all
                                          ${on ? "border-[#163321] bg-[#163321]/5 ring-2 ring-[#163321]/20"
                                               : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"}`}>
                      <div className="text-sm font-medium text-[#0F1F14]">{o.t}</div>
                      <div className="text-[11px] text-[#4A5E51] mt-0.5">{o.d}</div>
                    </button>
                  );
                })}
              </div>
              {autoFields.tipo_richiesta_auto === "supporto" && (
                <Textarea value={autoFields.damed_note_supporto}
                           onChange={(e) => setAutoFields({ ...autoFields,
                              damed_note_supporto: e.target.value })}
                           rows={2} maxLength={500}
                           placeholder="Raccontaci cosa ti serve capire (es. non so quale polizza scegliere)"
                           data-testid="input-damed-note-supporto"
                           className="mt-1 text-sm" />
              )}

              {/* Fase 5.4 — Help-text per ogni tipo richiesta (RCA classica / Bersani / Nuova immatricolazione) */}
              {autoFields.tipo_richiesta_auto === "rinnovo" && (
                <div
                  className="text-[11px] text-[#4A5E51] bg-[#F7F5EF] border-l-2 border-[var(--brand-accent,#163321)] px-3 py-2 rounded-r"
                  data-testid="help-tipo-rinnovo"
                >
                  <strong>RCA classica:</strong> rinnoviamo la tua polizza esistente mantenendo la classe di merito attuale.
                </div>
              )}
              {autoFields.tipo_richiesta_auto === "nuova_polizza" && (
                <div
                  className="text-[11px] text-[#4A5E51] bg-[#F7F5EF] border-l-2 border-[var(--brand-accent,#163321)] px-3 py-2 rounded-r"
                  data-testid="help-tipo-nuova"
                >
                  <strong>Nuova polizza:</strong> se hai già una polizza in famiglia con buona classe puoi ereditarla con la <strong>Legge Bersani</strong>. Se è una nuova immatricolazione senza classe familiare, si parte dalla 14ª classe.
                </div>
              )}
            </div>

            {/* ================================================================ */}
            {/* Fase 5.4 — Bersani esteso (No / Self / Familiare convivente)     */}
            {/* Mostrato SOLO se tipo_richiesta_auto=nuova_polizza e partner NON  */}
            {/* Leonardo (Leonardo ha già la sua sezione avanzata più in basso).  */}
            {/* ================================================================ */}
            {autoFields.tipo_richiesta_auto === "nuova_polizza" && partner?.id !== "leonardo" && (
              <div
                className="rounded-2xl border border-[#E5E2DB] bg-white p-4 space-y-3"
                data-testid="bersani-esteso-block"
              >
                <Label className="text-sm font-medium text-[#0F1F14]">
                  Vuoi applicare la Legge Bersani?
                </Label>
                <p className="text-[11px] text-[#4A5E51] -mt-1">
                  La Bersani ti permette di ereditare la classe di merito di un altro veicolo dello stesso tipo, tuo o di un familiare convivente.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { k: "no", t: "No, non applico Bersani", d: "Partirò dalla classe standard (14ª)" },
                    { k: "self", t: "Sì, targa mia", d: "Ho un altro veicolo dello stesso tipo intestato a me" },
                    { k: "familiare_convivente", t: "Sì, di un familiare convivente", d: "Erediterò la classe da un familiare (serve stato famiglia)" },
                  ].map((o) => {
                    const on = autoFields.bersani_source === o.k;
                    return (
                      <button
                        key={o.k}
                        type="button"
                        onClick={() => setAutoFields({ ...autoFields, bersani_source: o.k })}
                        data-testid={`radio-bersani-source-${o.k}`}
                        className={`text-left rounded-xl border p-3 transition-all ${
                          on
                            ? "border-[var(--brand-accent,#163321)] bg-[#163321]/5 ring-2 ring-[var(--brand-accent,#163321)]/20"
                            : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
                        }`}
                      >
                        <div className="text-sm font-medium text-[#0F1F14]">{o.t}</div>
                        <div className="text-[11px] text-[#4A5E51] mt-0.5">{o.d}</div>
                      </button>
                    );
                  })}
                </div>

                {/* Se familiare_convivente → info donatore */}
                {autoFields.bersani_source === "familiare_convivente" && (
                  <div
                    className="pt-3 border-t border-[#E5E2DB] space-y-3"
                    data-testid="bersani-donor-block"
                  >
                    <p className="text-[11px] font-semibold text-[#0F1F14]">
                      Dati del familiare da cui erediti la classe
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Nome familiare *</Label>
                        <Input
                          value={autoFields.bersani_donor.nome}
                          onChange={(e) =>
                            setAutoFields({
                              ...autoFields,
                              bersani_donor: { ...autoFields.bersani_donor, nome: e.target.value },
                            })
                          }
                          data-testid="input-bersani-donor-nome"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Cognome familiare *</Label>
                        <Input
                          value={autoFields.bersani_donor.cognome}
                          onChange={(e) =>
                            setAutoFields({
                              ...autoFields,
                              bersani_donor: { ...autoFields.bersani_donor, cognome: e.target.value },
                            })
                          }
                          data-testid="input-bersani-donor-cognome"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Grado di parentela *</Label>
                        <Select
                          value={autoFields.bersani_donor.parentela}
                          onValueChange={(val) =>
                            setAutoFields({
                              ...autoFields,
                              bersani_donor: { ...autoFields.bersani_donor, parentela: val },
                            })
                          }
                        >
                          <SelectTrigger className="h-10 text-sm" data-testid="select-bersani-donor-parentela">
                            <SelectValue placeholder="Seleziona parentela" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="coniuge">Coniuge</SelectItem>
                            <SelectItem value="padre">Padre</SelectItem>
                            <SelectItem value="madre">Madre</SelectItem>
                            <SelectItem value="figlio">Figlio</SelectItem>
                            <SelectItem value="figlia">Figlia</SelectItem>
                            <SelectItem value="fratello">Fratello</SelectItem>
                            <SelectItem value="sorella">Sorella</SelectItem>
                            <SelectItem value="altro">Altro convivente</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {/* Se lo stato famiglia non è già stato caricato per assicurato=familiare, chiedilo qui */}
                    {!statoFamigliaId && (
                      <FileUploadCard
                        label="Stato di famiglia *"
                        description="Certificato del Comune o autocertificazione — necessario per la Bersani da familiare convivente"
                        fileId={statoFamigliaId}
                        uploading={uploading.stato_famiglia}
                        onChange={(f) => uploadFile(f, "stato_famiglia")}
                        onClear={() => setStatoFamigliaId(null)}
                        testid="upload-stato-famiglia-bersani"
                      />
                    )}
                    {statoFamigliaId && (
                      <p className="text-[10px] text-emerald-700" data-testid="stato-famiglia-riuso">
                        ✓ Stato di famiglia già caricato (riusato).
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Fase 5.1 — TargaLookup con auto-fill + fallback manuale */}
            <TargaLookup
              value={veicolo}
              onChange={(v) => {
                setVeicolo({ ...veicolo, ...v });
                // retrocompat: propaga anche a form.extra
                if (v.targa !== undefined) updateExtra("targa", v.targa);
                if (v.marca || v.modello) {
                  updateExtra("modello", [v.marca || veicolo.marca, v.modello || veicolo.modello]
                    .filter(Boolean).join(" ").trim());
                }
              }}
              required
              dataTestId="input-targa-lookup"
            />

            {/* Fase 5.3 — 4 campi assuntivi (uso/km/CU/sinistri) RIMOSSI:
                le informazioni sono coperte in modo più strutturato e IVASS-compliant
                dal blocco DAMED più in basso. */}

            {/* Fase 5.4 — Blocco "Garanzie di interesse (multi-select)" RIMOSSO:
                verrà discusso in fase di preventivo/contatto commerciale, non nel
                lead iniziale, per ridurre la friction del form. */}

            {/* Fase 5.1 — Ricontatto preferito + fascia oraria (estratto v1.72.0 in ContattoPreferenzeBlock) */}
            <ContattoPreferenzeBlock
              className="pt-2"
              testidPrefix="select"
              value={{
                canale: autoFields.ricontatto_preferito || "",
                fascia: autoFields.ricontatto_fascia_oraria || "",
              }}
              onChange={(next) => setAutoFields({
                ...autoFields,
                ricontatto_preferito: next.canale,
                ricontatto_fascia_oraria: next.fascia,
              })}
            />

            {/* Fase 5.3 — Toggle "Carica documenti dopo" RIMOSSO.
                Documenti sempre opzionali server-side (documenti_dopo=true hardcoded). */}

            <div className="pt-1">
              <Label className="text-xs">Note aggiuntive (opzionale)</Label>
              <Textarea value={form.extra.note || ""}
                         onChange={e => updateExtra("note", e.target.value)}
                         rows={2} maxLength={300}
                         placeholder="Es. veicolo aziendale, garanzie particolari, ecc."
                         data-testid="input-note-auto" />
            </div>

            {/* ========================================================== */}
            {/* Fase 5.2 — DAMED IVASS: Analisi Assicurativa Tutela Circol. */}
            {/* ========================================================== */}
            <div className="rounded-2xl border border-[#4A5E51]/30 bg-[#F7F5EF] p-4 space-y-4"
                  data-testid="damed-block">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#163321]" />
                <h3 className="text-sm font-medium text-[#0F1F14]">
                  Analisi assicurativa (compliance IVASS)
                </h3>
              </div>
              <p className="text-[11px] text-[#4A5E51] -mt-2">
                Compilazione consigliata per una proposta accurata. Tutti i campi sono opzionali.
              </p>

              {/* Fase 5.3 — Rimossi da DAMED sezione 1:
                    - Data immatricolazione (recuperabile via lookup targa)
                    - Utilizzo del veicolo (Italia/SEE/Altri Paesi)
                    Restano invariate le sezioni DAMED 2-7. */}

              {/* DAMED 2 — Coperture ATTUALI */}
              <div>
                <Label className="text-xs">Coperture che possiedi ATTUALMENTE</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1"
                      data-testid="damed-coperture-attuali">
                  {[
                    ["rca_rcn", "RCA/RCN"], ["assistenza", "Assistenza"],
                    ["danni_ficva", "Furto/Incendio/Cristalli/Vandalismo"],
                    ["tutela_legale", "Tutela legale"], ["infortuni", "Infortuni"],
                    ["accessorie", "Garanzie accessorie"], ["nessuna", "Nessuna"],
                    ["altro", "Altro"],
                  ].map(([k, l]) => {
                    const on = autoFields.damed_coperture_attuali.includes(k);
                    return (
                      <label key={k}
                              className={`flex items-center gap-1.5 text-[11px] cursor-pointer px-2 py-1.5 rounded-lg border transition-colors
                                  ${on ? "border-[#163321] bg-white text-[#0F1F14]" : "border-[#E5E2DB] bg-white/60 text-[#4A5E51]"}`}>
                        <input type="checkbox" checked={on}
                                data-testid={`check-damed-attuale-${k}`}
                                onChange={(e) => {
                                  const list = e.target.checked
                                    ? [...autoFields.damed_coperture_attuali, k]
                                    : autoFields.damed_coperture_attuali.filter(x => x !== k);
                                  setAutoFields({ ...autoFields, damed_coperture_attuali: list });
                                }} />
                        {l}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* DAMED 3 — Coperture INTERESSE (15 opzioni raggruppate) */}
              <div>
                <Label className="text-xs">Coperture di INTERESSE</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1"
                      data-testid="damed-coperture-interesse">
                  {[
                    ["rca_rcn", "RCA/RCN"], ["linea_verde", "Linea verde (GPL/metano)"],
                    ["infortuni", "Infortuni"], ["indennita_patente", "Indennità patente"],
                    ["assistenza", "Assistenza"], ["furto", "Furto"],
                    ["tutela_legale", "Tutela legale"], ["incendio", "Incendio + Vandalismo"],
                    ["accessorie", "Garanzie accessorie"],
                    ["calamita_naturali", "Calamità naturali"],
                    ["aggiuntive", "Garanzie aggiuntive"], ["cristalli", "Cristalli"],
                    ["collisione_esteri", "Collisione veicoli esteri"],
                    ["speciale_difesa", "Speciale difesa"],
                    ["perdita_totale", "Perdita totale (natanti)"],
                  ].map(([k, l]) => {
                    const on = autoFields.damed_coperture_interesse.includes(k);
                    return (
                      <label key={k}
                              className={`flex items-center gap-1.5 text-[11px] cursor-pointer px-2 py-1.5 rounded-lg border transition-colors
                                  ${on ? "border-[#163321] bg-white text-[#0F1F14]" : "border-[#E5E2DB] bg-white/60 text-[#4A5E51]"}`}>
                        <input type="checkbox" checked={on}
                                data-testid={`check-damed-interesse-${k}`}
                                onChange={(e) => {
                                  const list = e.target.checked
                                    ? [...autoFields.damed_coperture_interesse, k]
                                    : autoFields.damed_coperture_interesse.filter(x => x !== k);
                                  setAutoFields({ ...autoFields, damed_coperture_interesse: list });
                                }} />
                        {l}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* DAMED 4 — Grado copertura RCA */}
              <div>
                <Label className="text-xs">Grado di copertura RCA</Label>
                <Select value={autoFields.damed_grado_copertura}
                        onValueChange={(val) => setAutoFields({ ...autoFields, damed_grado_copertura: val })}>
                  <SelectTrigger data-testid="select-damed-grado" className="h-10 text-sm">
                    <SelectValue placeholder="Seleziona massimale" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimo_legge">Minimo di legge (bipartito 6.450.000€ pers. + 1.300.000€ cose / tripartito 7.750.000€)</SelectItem>
                    <SelectItem value="minimo_bus">Minimo autobus (bipartito 30M€ persone + 2M€ cose)</SelectItem>
                    <SelectItem value="superiore">Massimale superiore al minimo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* DAMED 5, 6, 7 — Toggle SI/NO su tre righe */}
              <div className="space-y-2">
                <label className="flex items-start gap-2 p-2.5 rounded-lg border border-[#E5E2DB] bg-white/60 cursor-pointer">
                  <input type="checkbox" checked={autoFields.damed_rinuncia_rivalsa}
                          onChange={(e) => setAutoFields({ ...autoFields,
                              damed_rinuncia_rivalsa: e.target.checked })}
                          data-testid="check-damed-rinuncia-rivalsa" className="mt-0.5" />
                  <div className="text-[11px] text-[#4A5E51]">
                    <strong className="text-[#0F1F14]">Estensione RCA con rinuncia alla rivalsa</strong>
                    <br />(ebbrezza, stupefacenti su proprietario non conducente, trasporto non conforme, figli minori)
                  </div>
                </label>

                <label className="flex items-start gap-2 p-2.5 rounded-lg border border-[#E5E2DB] bg-white/60 cursor-pointer">
                  <input type="checkbox" checked={autoFields.damed_scatola_nera}
                          onChange={(e) => setAutoFields({ ...autoFields,
                              damed_scatola_nera: e.target.checked,
                              damed_guidabene_risparmi: e.target.checked ? autoFields.damed_guidabene_risparmi : false })}
                          data-testid="check-damed-scatola-nera" className="mt-0.5" />
                  <div className="text-[11px] text-[#4A5E51]">
                    <strong className="text-[#0F1F14]">Scatola nera (Full Box)</strong>
                    <br />Dispositivo elettronico per raccolta dati di guida
                  </div>
                </label>

                {autoFields.damed_scatola_nera && (
                  <label className="flex items-start gap-2 p-2.5 ml-6 rounded-lg border border-[#E5E2DB] bg-white/60 cursor-pointer">
                    <input type="checkbox" checked={autoFields.damed_guidabene_risparmi}
                            onChange={(e) => setAutoFields({ ...autoFields,
                                damed_guidabene_risparmi: e.target.checked })}
                            data-testid="check-damed-guidabene" className="mt-0.5" />
                    <div className="text-[11px] text-[#4A5E51]">
                      <strong className="text-[#0F1F14]">GuidaBene &amp; Risparmi</strong>
                      <br />Analisi stile guida per riduzione premio RCA
                    </div>
                  </label>
                )}
              </div>

              {/* DAMED 7 — Clausole facoltative (multi checkbox) */}
              <div>
                <Label className="text-xs">Clausole facoltative (solo autovetture)</Label>
                <div className="grid grid-cols-1 gap-1.5 mt-1">
                  {[
                    ["bonus_malus_franchigia", "Formula Bonus/Malus con franchigia",
                     "Riduzione premio RCA con partecipazione danni"],
                    ["conducente_esperto", "Conducente esperto",
                     "Riduzione premio RCA. ⚠ Conducenti < 26 anni possono avere inadeguatezza."],
                  ].map(([k, l, d]) => {
                    const on = autoFields.damed_clausole_facoltative.includes(k);
                    return (
                      <label key={k}
                              className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors
                                  ${on ? "border-[#163321] bg-white" : "border-[#E5E2DB] bg-white/60"}`}>
                        <input type="checkbox" checked={on}
                                data-testid={`check-damed-clausola-${k}`}
                                className="mt-0.5"
                                onChange={(e) => {
                                  const list = e.target.checked
                                    ? [...autoFields.damed_clausole_facoltative, k]
                                    : autoFields.damed_clausole_facoltative.filter(x => x !== k);
                                  setAutoFields({ ...autoFields, damed_clausole_facoltative: list });
                                }} />
                        <div className="text-[11px] text-[#4A5E51]">
                          <strong className="text-[#0F1F14]">{l}</strong>
                          <br />{d}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Fase 5.2 — Testo compliance IVASS finale */}
            <div className="text-[10px] text-[#7A8E83] italic bg-[#FAF9F5] border-l-2 border-[#4A5E51]/30 px-3 py-2 rounded-r"
                  data-testid="damed-compliance-note">
              <strong>N.B. IVASS:</strong> Alcune garanzie possono prevedere limitazioni ed esclusioni della prestazione.
              In particolare, qualora il prodotto oggetto dell'eventuale acquisto contenga garanzie malattie,
              la polizza potrebbe non operare per conseguenze di situazioni patologiche (infortuni/malattie)
              preesistenti all'acquisto. Il Suo intermediario è a disposizione per illustrarLe nel dettaglio
              ogni contenuto di polizza.
            </div>

            {/* Dati anagrafici aggiuntivi e matricola SOLO per Leonardo Auto */}
            {partner?.id === "leonardo" && (
              <div className="pt-4 border-t border-[#E5E2DB] space-y-4" data-testid="leonardo-extra-fields">
                <div className="bg-gradient-to-br from-emerald-50 to-[#163321]/5 border border-emerald-300 rounded-xl p-3.5 flex items-start gap-3" data-testid="banner-autoextract">
                  <span className="text-2xl leading-none">✨</span>
                  <div className="flex-1">
                    <p className="text-[12px] font-semibold text-emerald-900">Compilazione automatica dai documenti</p>
                    <p className="text-[10px] text-emerald-800/80 leading-relaxed mt-0.5">
                      Quando carichi <strong>busta paga, libretto e patente</strong>, leggiamo i dati per te (matricola, targa, anagrafica, residenza). Tu verifichi e correggi solo dove serve.
                    </p>
                  </div>
                </div>

                <div className="bg-[#163321]/5 border border-[#163321]/20 rounded-xl p-3">
                  <p className="text-[11px] font-semibold text-[#163321] mb-1">Dati per la delega Leonardo</p>
                  <p className="text-[10px] text-[#4A5E51]">
                    Servono per generare l'<strong>Atto di Delega</strong> per la trattenuta in busta paga (richiesto dalla tua società del gruppo Leonardo).
                  </p>
                </div>

                {/* === Q&A WIZARD per Leonardo Auto — linguaggio universale 12-80 anni === */}

                {/* Mini-checklist iniziale */}
                <div className="bg-white border border-[#E5E2DB] rounded-2xl p-4" data-testid="checklist-iniziale">
                  <p className="text-[11px] font-semibold text-[#163321] uppercase tracking-wider mb-2">📋 Cosa ti servirà (puoi caricare con il telefono)</p>
                  <ul className="text-[12px] text-[#4A5E51] space-y-1.5 pl-1">
                    <li>📄 La <strong>busta paga</strong> più recente</li>
                    <li>🪪 Il <strong>badge Leonardo</strong></li>
                    <li>🚗 Il <strong>libretto</strong> del veicolo (carta di circolazione)</li>
                    <li>🛂 La <strong>patente</strong> di chi guida</li>
                  </ul>
                </div>

                <Question
                  num="1"
                  title="Cosa devi fare?"
                  hint="Scegli la frase che descrive meglio la tua situazione. Se non sei sicuro, scegli l'ultima opzione."
                  testid="q-tipo-richiesta"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <QChoice
                      active={form.extra.tipo_richiesta === "rinnovo"}
                      onClick={() => updateExtra("tipo_richiesta", "rinnovo")}
                      title="🔁 Rinnovare la polizza"
                      subtitle="La mia polizza scade a breve, voglio una migliore"
                      testid="q-tipo-rinnovo"
                    />
                    <QChoice
                      active={form.extra.tipo_richiesta === "nuovo_acquisto"}
                      onClick={() => updateExtra("tipo_richiesta", "nuovo_acquisto")}
                      title="🛒 Ho appena comprato un veicolo"
                      subtitle="Mi serve la prima polizza per questo mezzo"
                      testid="q-tipo-nuovo"
                    />
                    <QChoice
                      active={form.extra.tipo_richiesta === "recupero_attestato"}
                      onClick={() => updateExtra("tipo_richiesta", "recupero_attestato")}
                      title="🕰 La mia polizza è scaduta da tempo"
                      subtitle="Veicolo fermo da mesi/anni, devo riassicurarlo"
                      testid="q-tipo-recupero"
                    />
                    <QChoice
                      active={form.extra.tipo_richiesta === "non_so"}
                      onClick={() => updateExtra("tipo_richiesta", "non_so")}
                      title="🤝 Aiutami a capire"
                      subtitle="Mi chiama un consulente Colucci e mi guida lui"
                      testid="q-tipo-non-so"
                    />
                  </div>
                  {form.extra.tipo_richiesta === "non_so" && (
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3 text-[12px] text-blue-900" data-testid="help-non-so">
                      Perfetto: nessuna scelta tecnica da fare. Compila qui sotto i tuoi dati di contatto e i documenti che hai a portata di mano. <strong>Un consulente Colucci ti chiamerà entro 24h</strong> e penserà a tutto lui.
                    </div>
                  )}
                </Question>

                {/* Campi specifici per RECUPERO ATTESTATO */}
                {form.extra.tipo_richiesta === "recupero_attestato" && (
                  <div className="bg-[#163321]/5 border border-[#163321]/20 rounded-xl p-4 space-y-3" data-testid="recupero-attestato-block">
                    <p className="text-xs font-semibold text-[#163321]">📜 Recupero del certificato della tua vecchia polizza</p>
                    <p className="text-[11px] text-[#4A5E51] leading-relaxed">
                      In Italia il <strong>certificato della vecchia polizza</strong> (che le compagnie chiamano "attestato di rischio") <strong>resta valido per 5 anni</strong> dalla scadenza. Se la tua polizza è scaduta da meno di 5 anni, lo recuperiamo noi gratis dalla compagnia di prima.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Compagnia di prima *</Label>
                        <Input
                          value={form.extra.compagnia_uscente || ""}
                          onChange={e => updateExtra("compagnia_uscente", e.target.value)}
                          placeholder="Es. Generali, UnipolSai, Genertel…"
                          data-testid="input-compagnia-uscente"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Quando è scaduta? *</Label>
                        <Input
                          type="date"
                          value={form.extra.scadenza_ultima_polizza || ""}
                          onChange={e => updateExtra("scadenza_ultima_polizza", e.target.value)}
                          data-testid="input-scadenza-ultima"
                        />
                        <p className="text-[10px] text-[#4A5E51] mt-1">Se non ricordi la data esatta, indica anche solo l'anno (es. 01/01/2023)</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* SEZIONE B: VEICOLO E CONTRAENTE */}
                {form.extra.tipo_richiesta && form.extra.tipo_richiesta !== "non_so" && (
                  <Question
                    num="2"
                    title="Di chi è il veicolo che vuoi assicurare?"
                    hint="Conta chi compare sul libretto come proprietario."
                    testid="q-proprietario"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <QChoice
                        active={form.extra.rc_familiare === false}
                        onClick={() => updateExtra("rc_familiare", false)}
                        title="👤 È mio"
                        subtitle="Sono io l'intestatario sul libretto"
                        testid="q-prop-me"
                      />
                      <QChoice
                        active={form.extra.rc_familiare === true}
                        onClick={() => updateExtra("rc_familiare", true)}
                        title="👨‍👩‍👧 È di un familiare con cui vivo"
                        subtitle="Coniuge, figlio, genitore convivente"
                        testid="q-prop-familiare"
                      />
                    </div>
                  </Question>
                )}

                {/* Q3 unificata: nuovo_acquisto chiede chi sarà il contraente perché manca la Q2 sul proprietario.
                    Per rinnovo/recupero deriviamo automaticamente da Q2. */}
                {form.extra.tipo_richiesta === "nuovo_acquisto" && form.extra.rc_familiare !== undefined && (
                  <Question
                    num="3"
                    title="Chi firma la polizza?"
                    hint="È la persona che compare sul contratto e a cui faremo la trattenuta in busta paga."
                    testid="q-contraente"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <QChoice
                        active={form.extra.contraente_diverso === false}
                        onClick={() => updateExtra("contraente_diverso", false)}
                        title="✍️ Io (dipendente Leonardo)"
                        testid="q-contr-me"
                      />
                      <QChoice
                        active={form.extra.contraente_diverso === true}
                        onClick={() => updateExtra("contraente_diverso", true)}
                        title="👨‍👩‍👧 Un familiare con cui vivo"
                        subtitle="La convenzione vale anche per i conviventi"
                        testid="q-contr-familiare"
                      />
                    </div>
                  </Question>
                )}
                {/* Per rinnovo/recupero: contraente = proprietario veicolo (auto-derivato) */}
                {form.extra.tipo_richiesta && form.extra.tipo_richiesta !== "nuovo_acquisto" && form.extra.rc_familiare !== undefined && form.extra.contraente_diverso === undefined && (() => {
                  // Imposta automaticamente contraente_diverso = rc_familiare per evitare la domanda ridondante
                  setTimeout(() => updateExtra("contraente_diverso", form.extra.rc_familiare), 0);
                  return null;
                })()}

                {/* Dati contraente (se diverso) */}
                {form.extra.contraente_diverso === true && (
                  <div className="bg-[#163321]/5 border border-[#163321]/20 rounded-xl p-4 space-y-3" data-testid="contraente-fields">
                    <p className="text-[11px] font-semibold text-[#163321]">Dati del contraente (familiare convivente)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Nome *</Label>
                        <Input value={form.extra.contraente_nome || ""} onChange={e => updateExtra("contraente_nome", e.target.value)} data-testid="input-contraente-nome" />
                      </div>
                      <div>
                        <Label className="text-xs">Cognome *</Label>
                        <Input value={form.extra.contraente_cognome || ""} onChange={e => updateExtra("contraente_cognome", e.target.value)} data-testid="input-contraente-cognome" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Codice fiscale *</Label>
                        <Input value={form.extra.contraente_codice_fiscale || ""} onChange={e => updateExtra("contraente_codice_fiscale", e.target.value.toUpperCase().replace(/\s/g, ""))} maxLength={16} data-testid="input-contraente-cf" />
                      </div>
                    </div>
                  </div>
                )}

                {/* SEZIONE C: CLASSE DI MERITO — copy ultra-semplice */}
                {(form.extra.contraente_diverso !== undefined || (form.extra.tipo_richiesta && form.extra.tipo_richiesta !== "nuovo_acquisto" && form.extra.tipo_richiesta !== "non_so" && form.extra.rc_familiare !== undefined)) && (
                  <Question
                    num="4"
                    title="C'è un'altra polizza in famiglia con buona classe di merito?"
                    hint={
                      form.extra.tipo_richiesta === "nuovo_acquisto"
                        ? "Per legge (Bersani) puoi prendere la classe di un altro veicolo dello stesso tipo, tuo o di un convivente."
                        : form.extra.tipo_richiesta === "recupero_attestato"
                        ? "Possiamo applicare anche la RC Familiare per ereditare la classe migliore del nucleo."
                        : "Per legge (RC Familiare) al rinnovo puoi ereditare la classe di un altro veicolo del nucleo, anche di tipo diverso (auto↔moto↔autocarro)."
                    }
                    testid="q-eredita"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <QChoice
                        active={form.extra.eredita_classe === true}
                        onClick={() => updateExtra("eredita_classe", true)}
                        title="✅ Sì, c'è"
                        subtitle="Voglio sfruttarla per pagare meno"
                        testid="q-eredita-si"
                      />
                      <QChoice
                        active={form.extra.eredita_classe === false}
                        onClick={() => updateExtra("eredita_classe", false)}
                        title="❌ No, oppure non so"
                        subtitle="Procedi con la polizza standard"
                        testid="q-eredita-no"
                      />
                    </div>
                    <details className="mt-2 text-[11px] text-[#4A5E51]">
                      <summary className="cursor-pointer text-[#163321] underline">Cos'è la "classe di merito"?</summary>
                      <p className="mt-1.5 leading-relaxed">
                        È un punteggio da 1 (migliore) a 18 (peggiore) che ogni assicuratore dà al guidatore in base agli anni senza incidenti con colpa. Più la classe è bassa, meno paghi. La legge italiana ti permette di "ereditare" una classe migliore da un'altra polizza del tuo nucleo familiare.
                      </p>
                    </details>
                  </Question>
                )}

                {/* Sotto-domande eredità classe */}
                {form.extra.eredita_classe === true && (
                  <div className="bg-[#FFF6EE] border border-[#D97B58]/30 rounded-xl p-4 sm:p-5 space-y-4" data-testid="eredita-classe-block">

                    {/* Targa veicolo donante — input dedicato e ben visibile */}
                    <div className="bg-white border-2 border-[#D97B58]/40 rounded-xl p-4">
                      <Label className="text-sm font-semibold text-[#7A4A2E] flex items-center gap-2 mb-2">
                        <Car className="w-4 h-4" />
                        Targa del veicolo donante
                        <span className="text-[#D97B58]">*</span>
                      </Label>
                      <p className="text-[11px] text-[#4A5E51] mb-2">
                        Inserisci la targa del veicolo da cui erediteremo la classe di merito.
                      </p>
                      <Input
                        value={form.extra.targa_veicolo_donante || ""}
                        onChange={e => updateExtra("targa_veicolo_donante", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
                        placeholder="ES. AB123CD"
                        maxLength={8}
                        className="h-11 text-lg tracking-[0.15em] font-mono uppercase text-center"
                        data-testid="input-targa-donante"
                      />
                    </div>

                    {/* Libretto del veicolo donante (targa base) */}
                    <FileUploadCard
                      label="Libretto del veicolo donante (targa base) *"
                      description="Carta di circolazione del veicolo da cui erediti la classe — fronte/retro PDF o foto"
                      fileId={librettoDonanteId}
                      uploading={uploading.libretto_donante}
                      onChange={f => uploadFile(f, "libretto_donante")}
                      onClear={() => setLibrettoDonanteId(null)}
                      testid="upload-libretto-donante"
                    />

                    {/* Documento d'identità del proprietario del veicolo donante */}
                    <FileUploadCard
                      label={
                        form.extra.donante_familiare_convivente
                          ? "Documento d'identità del familiare proprietario del veicolo donante *"
                          : "Documento d'identità (proprietario del veicolo donante) *"
                      }
                      description={
                        form.extra.donante_familiare_convivente
                          ? "Carta d'identità o patente del familiare convivente intestatario del veicolo — fronte/retro"
                          : "Se il veicolo donante è intestato a te, basta la tua carta d'identità (fronte/retro)"
                      }
                      fileId={docDonanteId}
                      uploading={uploading.doc_donante}
                      onChange={f => uploadFile(f, "doc_donante")}
                      onClear={() => setDocDonanteId(null)}
                      testid="upload-doc-donante"
                    />

                    {/* Q4b: Tipo veicolo donante */}
                    <Question
                      title="Che tipo di veicolo è quello donante?"
                      compact
                      testid="q-tipo-donante"
                    >
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: "auto", label: "Auto" },
                          { id: "moto", label: "Moto" },
                          { id: "autocarro", label: "Autocarro" },
                        ].map(o => (
                          <QChoice
                            key={o.id}
                            active={form.extra.tipo_veicolo_donante === o.id}
                            onClick={() => updateExtra("tipo_veicolo_donante", o.id)}
                            title={o.label}
                            testid={`q-tipo-donante-${o.id}`}
                          />
                        ))}
                      </div>
                      {form.extra.tipo_richiesta === "nuovo_acquisto" && form.extra.tipo_veicolo_donante && form.extra.tipo_veicolo && form.extra.tipo_veicolo_donante !== form.extra.tipo_veicolo && (
                        <p className="text-[10px] text-red-700 mt-2">
                          ⚠ Con la sola Legge Bersani non si può ereditare tra tipi diversi. Per farlo serve la RC Familiare (solo al rinnovo).
                        </p>
                      )}
                    </Question>

                    {/* Q4c: A chi è intestato il veicolo donante */}
                    <Question
                      title="A chi è intestato il veicolo donante (da cui prendiamo la classe)?"
                      hint="Se è di un familiare convivente, ti chiederemo lo stato di famiglia."
                      compact
                      testid="q-donante-intestatario"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <QChoice
                          active={form.extra.donante_familiare_convivente === false}
                          onClick={() => updateExtra("donante_familiare_convivente", false)}
                          title="A me"
                          testid="q-donante-me"
                        />
                        <QChoice
                          active={form.extra.donante_familiare_convivente === true}
                          onClick={() => updateExtra("donante_familiare_convivente", true)}
                          title="A un familiare convivente"
                          testid="q-donante-familiare"
                        />
                      </div>
                    </Question>

                    {/* Q4d: Sinistri ultimi 5 anni */}
                    <Question
                      title="Il veicolo donante ha avuto sinistri con colpa principale negli ultimi 5 anni?"
                      hint="Requisito normativo: se ci sono stati sinistri con colpa, l'eredità della classe non può essere applicata."
                      compact
                      testid="q-donante-sinistri"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <QChoice
                          active={form.extra.donante_no_sinistri_5anni === true}
                          onClick={() => updateExtra("donante_no_sinistri_5anni", true)}
                          title="No, nessun sinistro"
                          subtitle="Posso ereditare la classe"
                          testid="q-donante-sinistri-no"
                        />
                        <QChoice
                          active={form.extra.donante_no_sinistri_5anni === false}
                          onClick={() => updateExtra("donante_no_sinistri_5anni", false)}
                          title="Sì, c'è stato almeno uno"
                          subtitle="Eredità non applicabile"
                          testid="q-donante-sinistri-si"
                          variant="warning"
                        />
                      </div>
                    </Question>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Codice fiscale dipendente *</Label>
                    <Input
                      value={form.extra.codice_fiscale || ""}
                      onChange={e => updateExtra("codice_fiscale", e.target.value.toUpperCase().replace(/\s/g, ""))}
                      placeholder="RSSMRA80A01H501Z"
                      maxLength={16}
                      data-testid="input-codice-fiscale"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Indirizzo di residenza *</Label>
                    <Input
                      value={form.extra.indirizzo || ""}
                      onChange={e => updateExtra("indirizzo", e.target.value)}
                      placeholder="Via Roma, 12"
                      data-testid="input-indirizzo"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Località *</Label>
                    <ComuneAutocomplete
                      value={form.extra.localita || ""}
                      onChange={(v) => updateExtra("localita", v)}
                      placeholder="Napoli"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">CAP *</Label>
                      <Input
                        value={form.extra.cap || ""}
                        onChange={e => updateExtra("cap", e.target.value.replace(/\D/g, "").slice(0, 5))}
                        placeholder="80122"
                        maxLength={5}
                        data-testid="input-cap"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Prov. *</Label>
                      <Input
                        value={form.extra.provincia || ""}
                        onChange={e => updateExtra("provincia", e.target.value.toUpperCase().slice(0, 2))}
                        placeholder="NA"
                        maxLength={2}
                        data-testid="input-provincia"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Tipo veicolo *</Label>
                    <select
                      value={form.extra.tipo_veicolo || ""}
                      onChange={e => updateExtra("tipo_veicolo", e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-[#E5E2DB] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#163321]/30"
                      data-testid="input-tipo-veicolo"
                    >
                      <option value="">Seleziona...</option>
                      <option value="auto">Auto</option>
                      <option value="moto">Moto</option>
                      <option value="autocarro">Autocarro</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>
                </div>

                <div className="bg-[#FFF6EE] border-2 border-[#D97B58]/40 rounded-xl p-4 sm:p-5 space-y-3" data-testid="matricola-block">
                  <div>
                    <p className="text-sm font-semibold text-[#7A4A2E] flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Matricola dipendente
                    </p>
                    <p className="text-[11px] text-[#4A5E51] mt-1">
                      {extracted.busta_paga
                        ? <><span className="text-emerald-700 font-medium">✨ Compilata automaticamente dalla busta paga.</span> Verifica e correggi se necessario.</>
                        : "Verrà compilata automaticamente quando carichi la busta paga, oppure puoi inserirla manualmente."}
                    </p>
                  </div>
                  <Input
                    value={form.extra.matricola || ""}
                    onChange={e => updateExtra("matricola", e.target.value.replace(/[^A-Za-z0-9-]/g, "").slice(0, 20))}
                    placeholder="Es. 123456"
                    autoComplete="off"
                    className="h-11 text-base tracking-wide font-mono"
                    data-testid="input-matricola"
                  />
                </div>
              </div>
            )}

            {/* Upload obbligatori — 4 documenti per auto */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-[#E5E2DB]">
              <FileUploadCard
                label="Busta paga (ultimo mese) *"
                description="PDF o foto chiara"
                fileId={bustaPagaId}
                uploading={uploading.busta}
                onChange={f => uploadFile(f, "busta")}
                onClear={() => setBustaPagaId(null)}
                testid="upload-busta"
              />
              <FileUploadCard
                label="Badge aziendale *"
                description="Foto fronte/retro"
                fileId={badgeAziendaId}
                uploading={uploading.badge}
                onChange={f => uploadFile(f, "badge")}
                onClear={() => setBadgeAziendaId(null)}
                testid="upload-badge"
              />
              <FileUploadCard
                label="Libretto di circolazione *"
                description="PDF o foto chiara"
                fileId={librettoId}
                uploading={uploading.libretto}
                onChange={f => uploadFile(f, "libretto")}
                onClear={() => setLibrettoId(null)}
                testid="upload-libretto"
              />
              <FileUploadCard
                label="Patente di guida *"
                description="Fronte/retro, foto o PDF"
                fileId={patenteId}
                uploading={uploading.patente}
                onChange={f => uploadFile(f, "patente")}
                onClear={() => setPatenteId(null)}
                testid="upload-patente"
              />
            </div>
            {/* Stato di famiglia: obbligatorio per RC familiare, contraente diverso, o donante familiare */}
            {partner?.id === "leonardo" && (form.extra.rc_familiare || form.extra.contraente_diverso || (form.extra.eredita_classe && form.extra.donante_familiare_convivente)) && (
              <div className="pt-3 border-t border-[#E5E2DB]" data-testid="upload-stato-famiglia-section">
                <FileUploadCard
                  label="Certificato stato di famiglia *"
                  description="Documento rilasciato dal Comune o autocertificazione (PDF o foto)"
                  fileId={statoFamigliaId}
                  uploading={uploading.stato_famiglia}
                  onChange={f => uploadFile(f, "stato_famiglia")}
                  onClear={() => setStatoFamigliaId(null)}
                  testid="upload-stato-famiglia"
                />
                <p className="text-[10px] text-[#7A4A2E] mt-2">
                  Necessario per dimostrare la convivenza richiesta dalla normativa (Legge Bersani / RC Familiare / convenzione aziendale).
                </p>
              </div>
            )}
            <p className="text-[10px] text-[#4A5E51]">
              <Shield className="w-3 h-3 inline mr-1" />
              Documenti necessari per applicare lo sconto convenzione e calcolare correttamente la tariffa RC Auto.
            </p>
          </>
        )}

        {tipo.id === "famiglia" && (
          <div>
            <Label className="text-xs">Cosa ti interessa? (casa, vita, infortuni, salute...)</Label>
            <Textarea value={form.extra.note || ""} onChange={e => updateExtra("note", e.target.value)} rows={3} placeholder="Descrivi brevemente..." data-testid="input-note-famiglia" />
          </div>
        )}

        {tipo.id === "tempo_libero" && (
          <div>
            <Label className="text-xs">Cosa ti interessa? (viaggi, sport, hobby, animali...)</Label>
            <Textarea value={form.extra.note || ""} onChange={e => updateExtra("note", e.target.value)} rows={3} placeholder="Descrivi brevemente..." data-testid="input-note-tempo" />
          </div>
        )}
      </div>

      {/* Consensi obbligatori e facoltativi (MUP IVASS + GDPR) */}
      <div className="mt-6">
        <DichiarazioniBlock
          formData={{
            privacy: form.privacy,
            mup: form.mup,
            marketing: form.marketing,
            profilazione: form.profilazione,
          }}
          update={(field, val) => update(field, val)}
        />
      </div>
    </div>
  );
}

// ==================== Q&A Wizard primitives ====================
function SectionHeader({ letter, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-[#163321] to-[#224A32] text-white text-base font-bold flex items-center justify-center shadow-sm">
        {letter}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D97B58]">Sezione {letter}</p>
        <p className="text-sm font-heading font-medium text-[#0F1F14] leading-tight">{title}</p>
        {subtitle && <p className="text-[10px] text-[#4A5E51] mt-0.5">{subtitle}</p>}
      </div>
      <div className="hidden sm:block flex-1 h-px bg-gradient-to-r from-[#E5E2DB] to-transparent" />
    </div>
  );
}

function Question({ num, title, hint, children, compact, testid }) {
  return (
    <div
      className={`bg-white border border-[#E5E2DB] rounded-2xl ${compact ? "p-3" : "p-4 sm:p-5"} space-y-3 transition-shadow hover:shadow-sm`}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        {num && (
          <div className="shrink-0 w-7 h-7 rounded-full bg-[#163321] text-white text-xs font-bold flex items-center justify-center">
            {num}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className={`${compact ? "text-xs" : "text-sm"} font-semibold text-[#0F1F14] leading-snug`}>
            {title}
          </p>
          {hint && (
            <p className="text-[10px] text-[#4A5E51] mt-0.5 leading-relaxed">
              {hint}
            </p>
          )}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function QChoice({ active, onClick, title, subtitle, testid, variant = "default" }) {
  const palette = variant === "warning"
    ? {
      activeBorder: "border-amber-500", activeBg: "bg-amber-50", activeText: "text-amber-900",
      activeIcon: "bg-amber-500 text-white", idleBorder: "border-[#E5E2DB]", idleHover: "hover:border-amber-300",
    }
    : {
      activeBorder: "border-[#163321]", activeBg: "bg-[#163321]/5", activeText: "text-[#0F1F14]",
      activeIcon: "bg-[#163321] text-white", idleBorder: "border-[#E5E2DB]", idleHover: "hover:border-[#163321]/40",
    };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative w-full p-3 rounded-xl border-2 text-left transition-all ${
        active
          ? `${palette.activeBorder} ${palette.activeBg} shadow-sm`
          : `${palette.idleBorder} ${palette.idleHover} bg-white`
      }`}
    >
      {active && (
        <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center ${palette.activeIcon}`}>
          <CheckCircle2 className="w-3.5 h-3.5" />
        </div>
      )}
      <p className={`text-sm font-medium pr-6 ${active ? palette.activeText : "text-[#0F1F14]"}`}>{title}</p>
      {subtitle && <p className="text-[10px] text-[#4A5E51] mt-1 pr-6 leading-snug">{subtitle}</p>}
    </button>
  );
}


// ==================== File upload card ====================
function FileUploadCard({ label, description, fileId, uploading, onChange, onClear, testid }) {
  const inputRef = useRef(null);
  const isUploaded = !!fileId;

  return (
    <div className="border-2 border-dashed border-[#E5E2DB] rounded-xl p-4 text-center bg-white hover:border-[#163321]/40 transition-colors" data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf"
        capture="environment"
        className="hidden"
        onChange={e => onChange(e.target.files?.[0])}
      />
      {isUploaded ? (
        <div className="flex flex-col items-center gap-2">
          <FileCheck className="w-8 h-8 text-emerald-600" />
          <p className="text-sm font-medium text-[#0F1F14]">{label}</p>
          <p className="text-[10px] text-emerald-600">Caricato ✓</p>
          <button type="button" onClick={onClear} className="text-[10px] text-red-600 underline">Rimuovi</button>
        </div>
      ) : uploading ? (
        <div className="flex flex-col items-center gap-2 py-2">
          <Loader2 className="w-6 h-6 animate-spin text-[#163321]" />
          <p className="text-xs text-[#4A5E51]">Caricamento...</p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex flex-col items-center gap-2 w-full py-2"
        >
          <Upload className="w-6 h-6 text-[#4A5E51]" />
          <p className="text-sm font-medium text-[#0F1F14]">{label}</p>
          <p className="text-[10px] text-[#4A5E51]">{description}</p>
          <p className="text-[10px] text-[#163321] underline mt-1">Clicca per caricare</p>
        </button>
      )}
    </div>
  );
}

// ============================================================================
// FASE I — Selettore Famiglia (Casa/Infortuni/Vita/Salute)
// Presenta 4 card cliccabili; onSelect naviga al wizard corrispondente
// preservando partner/sede via query string.
// ============================================================================
function FamigliaBranchSelector({ partner, sede, onSelect }) {
  const RAMI = [
    {
      id: "casa",
      label: "Casa",
      desc: "Multirischio abitazione: incendio, furto, RC fabbricato, danni acqua, terremoto.",
      icon: Home,
      color: "#163321",
    },
    {
      id: "infortuni",
      label: "Infortuni",
      desc: "Copertura persona e nucleo familiare: invalidità permanente, ricovero, morte da infortunio.",
      icon: Shield,
      color: "#D97B58",
    },
    {
      id: "vita",
      label: "Vita",
      desc: "TCM, mista, rendita. Protezione mutuo, pensione integrativa, risparmio a lungo termine.",
      icon: Sparkles,
      color: "#7B5E3C",
    },
    {
      id: "salute",
      label: "Salute",
      desc: "Rimborso spese mediche, diagnostica avanzata, visite specialistiche, check-up preventivi.",
      icon: Heart,
      color: "#C9A448",
    },
  ];

  return (
    <div data-testid="convenzioni-famiglia-selector">
      <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-2">Quale polizza Famiglia?</h2>
      <p className="text-sm text-[#4A5E51] mb-6">
        Convenzione <span className="font-medium text-[#163321]">{partner?.name}</span>
        {sede ? ` · ${sede}` : ""} — scegli il ramo per procedere al questionario dedicato.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {RAMI.map((r) => {
          const Icon = r.icon;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onSelect(r.id)}
              className="text-left p-5 rounded-2xl border-2 border-[#E5E2DB] bg-white hover:border-[#163321]/40 hover:shadow-md transition-all group"
              data-testid={`famiglia-ramo-${r.id}`}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: `${r.color}18`, color: r.color }}
              >
                <Icon className="w-5 h-5" />
              </div>
              <p className="font-semibold text-[#0F1F14] text-base group-hover:text-[#163321]">{r.label}</p>
              <p className="text-[11px] text-[#4A5E51] mt-1 leading-relaxed">{r.desc}</p>
              <p className="text-[10px] text-[#D97B58] mt-3 font-medium flex items-center gap-1">
                Vai al questionario <ArrowRight className="w-3 h-3" />
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// FASE J — Selettore Tempo Libero (Viaggi attivo · Sport/Hobby/Animali coming soon)
// ============================================================================
function TempoLiberoBranchSelector({ partner, sede, onSelect }) {
  const RAMI = [
    {
      id: "viaggi",
      label: "Viaggi",
      desc: "Assistenza sanitaria, spese mediche, bagaglio, annullamento e rimpatrio per viaggi Italia/estero.",
      icon: Compass,
      color: "#163321",
      available: true,
    },
    {
      id: "sport",
      label: "Sport & Attività",
      desc: "Copertura infortuni sportivi, tutela legale, RC per attività amatoriali e agonistiche.",
      icon: Shield,
      color: "#7B5E3C",
      available: false,
    },
    {
      id: "hobby",
      label: "Hobby & Passioni",
      desc: "Attrezzatura, collezioni, strumenti musicali. Coperture personalizzate.",
      icon: Sparkles,
      color: "#D97B58",
      available: false,
    },
    {
      id: "animali",
      label: "Animali Domestici",
      desc: "RC verso terzi, spese veterinarie, malattie ereditarie. Cani, gatti e altri pet.",
      icon: Heart,
      color: "#C9A448",
      available: false,
    },
  ];

  return (
    <div data-testid="convenzioni-tempo-libero-selector">
      <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-2">Quale polizza Tempo Libero?</h2>
      <p className="text-sm text-[#4A5E51] mb-6">
        Convenzione <span className="font-medium text-[#163321]">{partner?.name}</span>
        {sede ? ` · ${sede}` : ""} — scegli il prodotto per procedere.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {RAMI.map((r) => {
          const Icon = r.icon;
          const isActive = r.available;
          if (!isActive) {
            // v1.70 UX fix — invece di button disabled, link a /consulenza con prodotto pre-compilato
            return (
              <Link
                key={r.id}
                to={`/consulenza?prodotto=${r.id}`}
                className="text-left p-5 rounded-2xl border-2 border-[#E5E2DB] bg-white hover:border-[#D97B58]/40 hover:shadow-md transition-all group cursor-pointer"
                data-testid={`tempo-libero-ramo-${r.id}`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${r.color}18`, color: r.color }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-[#D97B58] text-white px-2 py-0.5 rounded-full">
                    Su richiesta
                  </span>
                </div>
                <p className="font-semibold text-base text-[#0F1F14]">{r.label}</p>
                <p className="text-[11px] text-[#4A5E51] mt-1 leading-relaxed">{r.desc}</p>
                <p className="text-[10px] text-[#D97B58] mt-3 font-medium flex items-center gap-1">
                  Contattaci per preventivo <ArrowRight className="w-3 h-3" />
                </p>
              </Link>
            );
          }
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onSelect(r.id)}
              className="text-left p-5 rounded-2xl border-2 border-[#E5E2DB] bg-white hover:border-[#163321]/40 hover:shadow-md cursor-pointer group transition-all"
              data-testid={`tempo-libero-ramo-${r.id}`}
            >
              <div className="flex items-start justify-between">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                  style={{ backgroundColor: `${r.color}18`, color: r.color }}
                >
                  <Icon className="w-5 h-5" />
                </div>
              </div>
              <p className="font-semibold text-base text-[#0F1F14]">{r.label}</p>
              <p className="text-[11px] text-[#4A5E51] mt-1 leading-relaxed">{r.desc}</p>
              <p className="text-[10px] text-[#D97B58] mt-3 font-medium flex items-center gap-1">
                Vai al questionario <ArrowRight className="w-3 h-3" />
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
